import type { AdvisoryManifest } from "../types";

export type { AdvisoryManifest };

/**
 * One advisory record, exactly as OSV published it.
 *
 * `cvssScore` and `cvssRating` are the only derived fields: the score comes
 * from the published CVSS v3.1 formula and the rating from its published
 * rating table. Both are null when OSV published no scoreable vector, which is
 * the honest answer for those records rather than a default.
 */
export interface AdvisoryRecord {
  id: string;
  aliases: string[];
  summary: string;
  cvssVector: string | null;
  cvssScore: number | null;
  cvssRating: string | null;
  published: string;
  modified: string;
  fixedVersions: string[];
}

export interface AdvisoryDataset {
  manifest: AdvisoryManifest;
  advisories: AdvisoryRecord[];
  /** groupId:artifactId -> version -> advisory indices. No ranges. */
  exact: Record<string, Record<string, number[]>>;
  /** groupId:artifactId -> [advisory index, ranges][]. */
  ranges: Record<string, [number, string[][]][]>;
}

let cached: Promise<AdvisoryDataset> | null = null;

/** Where a reference has to be read from. See `assetSourceOf`. */
export type AssetSource = "inline" | "filesystem" | "network";

export function getAdvisoryDataset(): Promise<AdvisoryDataset> {
  cached ??= loadDataset();
  return cached;
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function isGzip(bytes: Uint8Array): boolean {
  return bytes[0] === 0x1f && bytes[1] === 0x8b;
}

async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream !== "function") {
    throw new Error("This runtime cannot decompress the advisory dataset.");
  }
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Read one dataset file, whatever shape the build handed us.
 *
 * Three shapes are legitimate, and each is told apart by the value itself
 * rather than by the environment it happens to be running in. That distinction
 * is the whole point: two of these look alike on a POSIX machine, because a
 * root-relative URL (`/assets/advisories.json-HASH.gz`, what both the dev
 * server and the browser bundle emit) and an absolute path
 * (`/Users/.../data/advisories.json.gz`) both begin with a slash. Deciding by
 * shape is the only way to be right in all four build shapes at once.
 *
 *   data:    the standalone scanner, where the build wrote the bytes inline
 *   file:    running from source under Node, where the data sits on disk
 *   other:   anything fetch() can resolve, which covers absolute http(s) and
 *            root-relative paths alike, because fetch resolves the latter
 *            against the current origin
 *
 * Whether the bytes are still compressed is decided by the gzip magic number
 * rather than by headers, because that is the only thing that is true in
 * every case: a dev server that sees the .gz extension serves it with
 * Content-Encoding: gzip and the browser has already decompressed it, while
 * every other shape delivers the file exactly as stored.
 */
export function assetSourceOf(reference: string): AssetSource {
  if (reference.startsWith("data:")) return "inline";
  if (reference.startsWith("file:")) return "filesystem";
  return "network";
}

async function readAsset(reference: string): Promise<Uint8Array> {
  let bytes: Uint8Array;

  switch (assetSourceOf(reference)) {
    case "inline": {
      const comma = reference.indexOf(",");
      if (comma < 0) throw new Error("Malformed advisory data URL.");
      bytes = base64ToBytes(reference.slice(comma + 1));
      break;
    }
    case "filesystem": {
      const { readFile } = await import("node:fs/promises");
      const { fileURLToPath } = await import("node:url");
      bytes = new Uint8Array(await readFile(fileURLToPath(reference)));
      break;
    }
    case "network": {
      const response = await fetch(reference);
      if (!response.ok) {
        throw new Error(
          `Advisory dataset asset returned ${response.status}`,
        );
      }
      bytes = new Uint8Array(await response.arrayBuffer());
      break;
    }
  }

  return isGzip(bytes) ? gunzip(bytes) : bytes;
}

async function readJson(reference: string): Promise<unknown> {
  return JSON.parse(new TextDecoder().decode(await readAsset(reference)));
}

type AssetReferences = Record<keyof typeof ADVISORY_FILES, string>;

const ADVISORY_FILES = {
  advisories: "advisories.json.gz",
  exact: "exact-maven.json.gz",
  ranges: "ranges-maven.json.gz",
  manifest: "manifest.json",
} as const;

/**
 * Decide what to read, not how to read it.
 *
 * Under a bundler the `?url` imports give whatever that build shape produces.
 * Under plain Node (tests and `node --import tsx`) there is no `?url`
 * transform and the import cannot resolve at all, so the data directory
 * beside this module is used instead.
 *
 * That fallback returns a file: URL rather than a bare path on purpose. A bare
 * absolute path and a root-relative asset URL are indistinguishable to the
 * reader on a POSIX machine, and guessing between them is what previously
 * sent bundler URLs to the filesystem. Naming the scheme removes the question.
 */
async function resolveAssetReferences(): Promise<AssetReferences> {
  try {
    const { advisoryAssets } = await import("./assets");
    return advisoryAssets;
  } catch {
    const { join, dirname } = await import("node:path");
    const { fileURLToPath, pathToFileURL } = await import("node:url");
    const here = dirname(fileURLToPath(import.meta.url));
    return Object.fromEntries(
      Object.entries(ADVISORY_FILES).map(([key, file]) => [
        key,
        pathToFileURL(join(here, "data", file)).href,
      ]),
    ) as AssetReferences;
  }
}

async function loadDataset(): Promise<AdvisoryDataset> {
  // Imported dynamically so the data stays in its own chunk for the browser
  // and is only fetched when a scan has dependencies to check.
  const assets = await resolveAssetReferences();
  const [advisories, exact, ranges, manifest] = await Promise.all([
    readJson(assets.advisories),
    readJson(assets.exact),
    readJson(assets.ranges),
    readJson(assets.manifest),
  ]);
  return {
    manifest: manifest as AdvisoryManifest,
    advisories: advisories as AdvisoryRecord[],
    exact: exact as Record<string, Record<string, number[]>>,
    ranges: ranges as Record<string, [number, string[][]][]>,
  };
}
